import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss, colors } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Popover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { parse, unparse } from 'papaparse';
import React, { useCallback, useMemo, useState, useContext } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { createMenuFromCsv } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import { Typography as T } from 'common/typography';
import { downloadEncodedCsv, localize } from 'common/utils';
import { WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';
import { toSheet, uploadToGoogleSheets } from 'modules/menu/google-sheets/googleSheets';
import { trackCreateItemsFromCsv, trackSelectCSVForImport } from 'modules/menu/hooks/telemetryHooks';

import CsvFileDropzone from './CsvFileDropzone';
import { FileSelectedProps, GoogleModeSwitchButton, GoogleSelectZone } from './GoogleSheetsSelectZone';
import { getNumberFormats, getWidths } from './gsFormats';
import { getValidations } from './gsValidations';
import { CSVColumnKey } from './types';
import { useIsGoogleSheetsEnabled } from './useIsGoogleSheetsEnabled';
import { parseDelimiter } from './utils';
import { MultiLevelSelect } from '../components/MultiLevelSelect';
import useMenuData from '../useMenuData';
import { flattenCategories } from '../utils';

type Props = {
  menuId: string;
  country: string;
  mode: 'catalog' | 'menu';
  csvFields: {
    name: string;
    exampleValue: string;
    required: boolean;
    info: string | null;
  }[];
  language: Language;
  isOpen: boolean;
  onClose: (reloadMenu?: boolean) => void;
  onSpreadsheetSelectStart: () => void;
  onSpreadsheetSelectEnd: () => void;
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow: auto;
`;

const StyledWoltModal = styled(WoltModal)`
  width: 480px;
  background: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
  border-radius: 8px;
`;

const TitleContainer = styled.div`
  ${typographyCss.Body3};
  display: flex;
  flex-direction: column;
`;

const DownloadFiles = styled.div`
  margin-block-start: 8px;
  align-self: flex-end;
  height: 24px;
`;

const FieldLine = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CodeWrapper = styled.code`
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  margin-block-start: 8px;
  padding: 8px 16px;
  font-size: 12px;
  line-height: 14px;
  border-radius: 8px;
`;

const InfoIcon = styled(InfoCircleFilled)`
  margin-inline-start: 4px;
  margin-block-end: -2px;
  height: 10px;
  width: 10px;
  fill: ${cbSemanticCssVars.textSecondary};
`;

const RequiredField = styled.div`
  color: ${cbSemanticCssVars.textSecondary};
`;

const CategoryLabel = styled.div`
  ${typographyCss.Body3};
  margin-block-start: 16px;
  margin-block-end: 8px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const ButtonContainer = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const ErrorList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow: auto;
`;

const ErrorListItem = styled.li`
  margin: 0;
  padding: 0;
`;

type ValidationErrorData = {
  response: {
    data: {
      errors: { [key: number]: string[] };
    };
  };
};

const createCategoryOption = (category: Category, parentId: null | string, primaryLanguage: string) => ({
  value: category.id,
  label: localize(category.name, primaryLanguage),
  parentId,
  childrenIds: category.child_categories.map(subcategory => subcategory.id),
});

const isValidationErrorData = (error: any): error is ValidationErrorData => {
  const errors = error?.response?.data?.errors;
  if (!errors) {
    return false;
  }
  const errorKeys = Object.keys(errors);
  return error?.response?.status === 422 && errorKeys.length > 0 && errorKeys.every(key => !isNaN(parseFloat(key)));
};

const MULTIPLE_CATEGORIES = 'MULTIPLE_CATEGORIES';
const NO_CATEGORY = 'NO_CATEGORY';

const CSVItemImportModal = ({
  menuId,
  country,
  mode,
  csvFields,
  language,
  isOpen,
  onClose,
  onSpreadsheetSelectStart = () => null,
  onSpreadsheetSelectEnd = () => null,
}: Props) => {
  const isGoogleSheetsEnabled = useIsGoogleSheetsEnabled();
  const intl = useIntl();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [file, setFile] = useState<File>();
  const [selectedSpreadSheet, setSelectedSpreadsheet] = useState<FileSelectedProps>();
  const [hasError, setHasError] = useState<boolean>();
  const [hasCategoryId, setHasCategoryId] = useState(false);
  const [googleSheetsMode, setGoogleSheetsMode] = useState<boolean>(false);
  const [templateCreating, setTemplateCreating] = useState<boolean>(false);

  const {
    mutateAsync: handleSubmit,
    error: submitError,
    isLoading: submitting,
    reset,
  } = useMutation(
    [file?.name, selectedCategory],
    async () => {
      if (!file || selectedCategory === null) {
        return;
      }
      const result = await createMenuFromCsv({
        menuId,
        csvFile: file,
        categoryId:
          selectedCategory === MULTIPLE_CATEGORIES || selectedCategory === NO_CATEGORY ? undefined : selectedCategory,
        catalogCountry: mode === 'catalog' ? country : undefined,
      });
      toast.success(intl.formatMessage({ id: 'menu.csvItemImport.fileImportSuccessful' }, { file: file.name }));
      if (result?.unsynced_items && result.unsynced_items.length > 0) {
        toast(
          <>
            <FormattedMessage id="menu.csvItemImport.unsyncedItemsWarning" />
            <ErrorList>
              {result.unsynced_items.map(item => (
                <ErrorListItem key={item}>{item}</ErrorListItem>
              ))}
            </ErrorList>
          </>,
          {
            duration: Infinity,
          },
        );
      }
      onClose(true);
    },
    {
      onSuccess: () => trackCreateItemsFromCsv('Success', selectedCategory ?? ''),
      onError: error => trackCreateItemsFromCsv(`Failure: ${error}`, selectedCategory ?? ''),
    },
  );

  const { categories } = useMenuData();

  const categoryOptions = useMemo(
    () =>
      categories
        .filter(category => category.id !== WOLT_SPECIALS_CATEGORY_ID)
        .flatMap(category => [
          createCategoryOption(category, null, language),
          ...category.child_categories.map(subcategory => createCategoryOption(subcategory, category.id, language)),
        ])
        .concat({
          label: intl.formatMessage({ id: 'menu.csvItemImport.noCategory' }),
          value: NO_CATEGORY,
          parentId: null,
          childrenIds: [],
        }),

    [categories, intl, language],
  );

  const exampleFields = useMemo(
    () =>
      csvFields.reduce<Record<string, string | number>>((acc, curr) => {
        if (curr.exampleValue.length > 0 && !isNaN(Number(curr.exampleValue))) {
          acc[curr.name] = Number(curr.exampleValue);
        } else {
          acc[curr.name] = curr.exampleValue;
        }
        return acc;
      }, {}),
    [csvFields],
  );

  const { currency } = useContext(CurrencyContext);

  const handleInitializeGoogleSheetsExample = useCallback(async () => {
    setTemplateCreating(true);
    const spreadsheetName = `items-template-${DateTime.local().toISODate()}`;
    const categoryData = flattenCategories(categories).map(category => ({
      category_id: !!category.child_categories.length ? '' : category.id,
      category_name: localize(category.name, language),
    }));
    const selectedColumns = Object.keys(exampleFields) as Partial<CSVColumnKey>[];
    const numberFormats = getNumberFormats(currency, selectedColumns);
    const validations = getValidations(selectedColumns);
    const widths = getWidths(selectedColumns);

    await uploadToGoogleSheets(
      spreadsheetName,
      toSheet('items', [exampleFields], numberFormats, validations, widths),
      toSheet('categories', categoryData),
    )
      .then(spreadsheet => spreadsheet?.spreadsheetUrl)
      .then(url => window.open(url, '_blank')?.focus())
      .catch(() => setHasError(true))
      .finally(() => setTemplateCreating(false));
  }, [categories, exampleFields, currency, language]);

  const onGoogleSpreadsheetSelected = useCallback(({ file, result, data }: FileSelectedProps) => {
    try {
      setSelectedSpreadsheet({ file, result, data });
      const hasCategory = !!data.find(line => line.category_id);
      setHasCategoryId(hasCategory);
      setSelectedCategory(hasCategory ? MULTIPLE_CATEGORIES : null);
      setFile(file);
    } catch (_) {
      setHasError(true);
    }
  }, []);

  const handleDownloadExample = useCallback(async () => {
    const csvData = unparse([exampleFields]);
    downloadEncodedCsv(csvData, 'text/csv', 'csv-import-example.csv', language === 'ja');
  }, [exampleFields, language]);

  const handleDownloadCategories = useCallback(async () => {
    const categoryData = flattenCategories(categories).map(category => ({
      category_id: !!category.child_categories.length ? '' : category.id,
      category_name: localize(category.name, language),
    }));
    const csvData = unparse(categoryData);
    downloadEncodedCsv(csvData, 'text/csv', 'csv-categories-list.csv', language === 'ja');
  }, [categories, language]);

  return (
    <StyledWoltModal isOpen={isOpen} onRequestClose={() => onClose()}>
      <ModalHeader onCloseClick={() => onClose()}>
        {isGoogleSheetsEnabled && (
          <FormattedMessage
            id={mode === 'menu' ? 'menu.csvItemImport.importItems' : 'menu.csvItemImport.importFeedItems'}
          />
        )}
        {!isGoogleSheetsEnabled && (
          <FormattedMessage
            id={mode === 'menu' ? 'menu.csvItemImport.importItemsFromCsv' : 'menu.csvItemImport.importFeedItemsFromCsv'}
          />
        )}
      </ModalHeader>
      {isGoogleSheetsEnabled && <GoogleModeSwitchButton value={googleSheetsMode} onChange={setGoogleSheetsMode} />}

      <Content>
        <TitleContainer>
          <FormattedMessage id="menu.csvItemImport.importFields" />
          {googleSheetsMode ? (
            <DownloadFiles>
              <FormattedMessage
                id="menu.googleSheets.initTemplate"
                values={{
                  button: chunks => (
                    <TextButton
                      disabled={templateCreating}
                      variant="blue"
                      onClick={handleInitializeGoogleSheetsExample}
                    >
                      {templateCreating && <Spinner size={'small'} />} {chunks}
                    </TextButton>
                  ),
                }}
              />
            </DownloadFiles>
          ) : (
            <DownloadFiles>
              <FormattedMessage
                id="menu.csvItemImport.downloadResources"
                values={{
                  fileButton: chunks => (
                    <TextButton variant="blue" onClick={handleDownloadExample}>
                      {chunks}
                    </TextButton>
                  ),
                  categoryButton: chunks => (
                    <TextButton variant="blue" onClick={handleDownloadCategories}>
                      {chunks}
                    </TextButton>
                  ),
                }}
              />
            </DownloadFiles>
          )}
        </TitleContainer>

        <CodeWrapper>
          {csvFields.map(field => (
            <FieldLine key={field.name}>
              <div>
                {field.name}
                {field.info && (
                  <Popover content={<T.Small>{field.info}</T.Small>} contentZIndex={100002} placement="right">
                    <InfoIcon />
                  </Popover>
                )}
              </div>
              {field.required && <RequiredField>required</RequiredField>}
            </FieldLine>
          ))}
        </CodeWrapper>
        {googleSheetsMode ? (
          <GoogleSelectZone
            hasError={hasError}
            onFileSelected={onGoogleSpreadsheetSelected}
            onStartSelect={onSpreadsheetSelectStart}
            onEndSelect={onSpreadsheetSelectEnd}
            fileSelected={selectedSpreadSheet ?? undefined}
          />
        ) : (
          <CsvFileDropzone
            fileName={file?.name}
            disabled={submitting}
            hasError={hasError}
            onDrop={async files => {
              setHasError(false);
              reset();
              if (!files[0].name.endsWith('.csv')) {
                setHasError(true);
                trackSelectCSVForImport('Failed: wrong file extension');
              }
              const delimiter = await parseDelimiter(files[0]);
              parse(files[0], {
                header: true,
                delimiter,
                complete: results => {
                  trackSelectCSVForImport('Success');
                  if (results.meta.fields?.includes('category_id')) {
                    setHasCategoryId(true);
                    setSelectedCategory(MULTIPLE_CATEGORIES);
                  }
                },
                error: err => trackSelectCSVForImport(`Failed: ${err.message}`),
              });
              setFile(files[0]);
            }}
          />
        )}

        {!googleSheetsMode && hasError && (
          <InputAlert style={{ marginBlockStart: 8 }} variant="error">
            <FormattedMessage id="menu.csvItemImport.fileMustBeCsv" />
          </InputAlert>
        )}

        {googleSheetsMode && hasError && (
          <InputAlert style={{ marginBlockStart: 8 }} variant="error">
            <FormattedMessage id="menu.fileImportFailed.fileMustBeCsv" />
          </InputAlert>
        )}

        <CategoryLabel>
          <FormattedMessage id="menu.csvItemImport.categoryToImport" />
        </CategoryLabel>
        <MultiLevelSelect
          menuPlacement="top"
          caretPosition="right"
          options={
            hasCategoryId
              ? [
                  {
                    label: intl.formatMessage({ id: 'menu.csvItemImport.multipleCategories' }),
                    value: MULTIPLE_CATEGORIES,
                    childrenIds: [],
                    parentId: null,
                  },
                ]
              : categoryOptions
          }
          onChange={value => setSelectedCategory(value)}
          placeholder={intl.formatMessage({ id: 'menu.csvItemImport.selectCategory' })}
          value={selectedCategory}
          disabled={submitting || hasCategoryId}
        />
      </Content>

      <StyledModalFooter>
        {!!submitError && !isValidationErrorData(submitError) && (
          <APIErrorNotification style={{ marginBlockEnd: 16 }} error={submitError} />
        )}
        {!!submitError && isValidationErrorData(submitError) && (
          <Alert
            style={{ marginBlockEnd: 16 }}
            variant="error"
            title={intl.formatMessage({ id: 'menu.csvItemImport.fileImportFailed' }, { file: file?.name })}
            showMoreContent={
              <ErrorList>
                {Object.entries(submitError.response.data.errors).map(([key, value]) => {
                  const readableValue = value.join('; ');
                  return (
                    <ErrorListItem key={`${key}-${readableValue}`}>
                      <FormattedMessage id="menu.csvItemPriceUpdate.rowLabel" values={{ row: Number(key) + 1 }} />:{' '}
                      {readableValue}
                    </ErrorListItem>
                  );
                })}
              </ErrorList>
            }
          >
            <FormattedMessage
              id="menu.csvItemPriceUpdate.importErrorMessage"
              values={{ count: Object.keys(submitError.response.data.errors).length }}
            />
          </Alert>
        )}
        <ButtonContainer>
          <Button size="small" variant="lightBlue" onClick={() => onClose()} disabled={submitting}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            style={{ marginInlineStart: 8 }}
            disabled={!file || selectedCategory === null || hasError || submitting}
            loading={submitting}
            size="small"
            variant="blue"
            onClick={() => handleSubmit()}
          >
            <FormattedMessage id="state.submit" />
          </Button>
        </ButtonContainer>
      </StyledModalFooter>
    </StyledWoltModal>
  );
};

export default CSVItemImportModal;
